/**
 * Utility for creating duration values in seconds and milliseconds.
 * Primarily used for cookie TTLs and other time-based configurations.
 *
 * - Use `DurationSeconds` for client-side `setCookie()` (which expects seconds)
 * - Use `DurationMs` for Express `res.cookie({ maxAge })` (which expects milliseconds)
 */

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR
const SECONDS_PER_YEAR = 365 * SECONDS_PER_DAY
const MS_PER_SECOND = 1000

/**
 * Convert minutes to seconds
 */
export function minutesInSeconds(minutes: number): number {
  return minutes * SECONDS_PER_MINUTE
}

/**
 * Convert hours to seconds
 */
export function hoursInSeconds(hours: number): number {
  return hours * SECONDS_PER_HOUR
}

/**
 * Convert days to seconds
 */
export function daysInSeconds(days: number): number {
  return days * SECONDS_PER_DAY
}

/**
 * Convert years to seconds
 */
export function yearsInSeconds(years: number): number {
  return years * SECONDS_PER_YEAR
}

// Common duration constants in seconds (for client-side setCookie)
export const DurationSeconds = {
  THREE_DAYS: daysInSeconds(3),
  THIRTY_DAYS: daysInSeconds(30),
  THIRTY_MINUTES: minutesInSeconds(30),
  SIXTY_DAYS: daysInSeconds(60),
  ONE_YEAR: yearsInSeconds(1),
  TWO_YEARS: yearsInSeconds(2),
  TEN_YEARS: yearsInSeconds(10),
} as const

// Common duration constants in milliseconds (for Express res.cookie maxAge)
export const DurationMs = {
  THIRTY_MINUTES: DurationSeconds.THIRTY_MINUTES * MS_PER_SECOND,
  THIRTY_DAYS: DurationSeconds.THIRTY_DAYS * MS_PER_SECOND,
  ONE_YEAR: DurationSeconds.ONE_YEAR * MS_PER_SECOND,
} as const
